<template>
  <panel :title="contest.contestName.$value">
    <template slot="toolbar">
      <check-permission v-if="!contest.contestId.$model" permissions="CONTEST.ADD">
        <div class="dib">
          <span class="text-warning mr-2">Konkurs niezaakceptowany</span>

          <button class="btn btn-secondary mr-5" @click="openModal">
            Zmień dane konkursu
          </button>
        </div>
      </check-permission>

      <w-row-buttons :model="contest" :move-up="false" :move-down="false" />
    </template>

    <div class="row form-group">
      <div class="col-6">
        <w-radio-list :model="contest.diplomaType" :values="diplomaTypes" />
      </div>

      <div class="col-6">
        <yes-or-no :model="contest.scholarshipApplication" />
      </div>
    </div>

    <div :key="'contestsTeachers_' + index" class="row form-group teachers-table">
      <div class="col-12">
        <w-form-table>
          <thead>
            <tr>
              <th class="middle" colspan="6">{{ contest.teacher.$schema.title }}</th>
            </tr>

            <tr>
              <th class="middle">Lp.</th>

              <th class="middle">Płeć</th>

              <th class="middle">Imię</th>

              <th class="middle">
                Nazwisko
                <w-tooltip>
                  {{ contest.teacher._internal.schema.items.properties.lastName.description }}
                </w-tooltip>
              </th>

              <th class="middle">Pełna nazwa szkoły</th>

              <th class="middle">
                <span class="sr-only">Akcje</span>
              </th>
            </tr>
          </thead>

          <tbody :ref="'tbody_' + index">
            <teacher-row
              v-for="(teacher, indexT) in contest.teacher.$items"
              :key="'teacher_' + index + '.' + indexT"
              :item="teacher.$item"
              :index="indexT"
            />
          </tbody>

          <tfoot>
            <tr>
              <td colspan="6" class="text-right">
                <w-add-row :model="contest.teacher" @rowAdded="setFocusOnLastChildInput('tbody_' + index)">
                  Dodaj nauczyciela-opiekuna
                </w-add-row>
              </td>
            </tr>
          </tfoot>
        </w-form-table>

        <w-error :model="contest.teacher" />
      </div>
    </div>

    <attachments
      :additional-metadata="{ contest: contest.guid.$model }"
      :entity-id="documentId"
      :entity-type="EntityType.DOCUMENT"
      :schema="$schema"
      :show-radio="false"
      :default-description="defaultDescription"
      :show-add-from-mdok-button="true"
      @attachmentAdded="addAttachmentToSchema"
      @attachmentRemoved="removeAttachmentFromSchema"
    />

    <w-warnings v-if="validated && validateAttachments" :model="$schema" :display-errors="true" />

    <slot name="contest" :contest="contest" />

    <contest-modal
      :show="showModal"
      :hide-selects="true"
      :use-name-select="true"
      @save="saveContest"
      @close="closeModal"
    />
  </panel>
</template>

<script>
import components from '@witkac/bootstrap4-components/src/components/formComponents';
import { mapState } from 'vuex';

import Attachments from '@/components/formComponents/attachment/Attachments';
import ContestModal from '@/components/modals/ContestModal';
import Panel from '@/components/panel/Panel';
import { ContestType } from '@/enums/contestType.enum';
import { EntityType } from '@/enums/entityType.enum';
import { Errors } from '@/enums/errors.enum';
import { Options } from '@/enums/options.enum';
import { SchoolLevel } from '@/enums/schoolLevel.enum';
import { Successes } from '@/enums/successes.enum';
import { formMixin } from '@/mixins/form';
import { methodsMixin } from '@/mixins/methods';
import { notificationsMixin } from '@/mixins/notifications';
import schema from '@/schemas/contestAttachments.schema';

import TeacherRow from './TeacherRow.vue';
import YesOrNo from './YesOrNoRadio.vue';

// @vuese
// Konkurs
// @group Forms
export default {
  components: { Attachments, ContestModal, Panel, TeacherRow, YesOrNo },
  mixins: [formMixin, methodsMixin, notificationsMixin],
  schema,
  props: {
    // Konkurs
    contest: { type: Object, required: false },
    // Schema
    schema: { type: Object, required: false },
    // Index konkursu
    index: { type: Number, required: true }
  },
  data() {
    return { model: {}, EntityType, showModal: false };
  },

  computed: {
    documentId() {
      return this.schema.id?.$model.toString() || this.$route.params.documentId.toString();
    },
    validated() {
      return this.schema.id?.$dirty;
    },
    defaultDescription() {
      const description = [this.schema?.$model?.data?.student?.fullName, this.contest?.$model?.contestName];
      return description.filter(part => !!part).join(' - ');
    },
    ...mapState({
      attachments(state) {
        return state.document.attachment.list.filter(
          attachment =>
            attachment.entityId === this.documentId &&
            attachment.entityType === EntityType.DOCUMENT &&
            attachment.metadata.contest === this.contest.guid.$model
        );
      },
      diplomaTypes: state => {
        const dictionary = state.optionValue.options[Options.DICTIONARY].value;
        return dictionary && JSON.parse(dictionary).diplomaTypes;
      },
      validateAttachments: state => state.optionValue.options[Options.VALIDATE_ATTACHMENTS]?.value === 'true'
    })
  },

  mounted() {
    this.$schema.attachments.$setData(this.attachments);
  },

  methods: {
    // @vuese
    // Otwiera modala edycji konkursu
    openModal() {
      this.$store.commit('contest/setEntity', {
        name: this.contest.contestName.$model,
        declination: {
          miejscownik: this.contest.contestNameMsc.$model,
          dopelniacz: this.contest.contestNameDop.$model
        },
        level: SchoolLevel[this.schema.data.school.level.$model],
        type: ContestType[this.schema.data.contestType.$model]
      });
      this.showModal = true;
    },
    // @vuese
    // Zamyka modala edycji konkursu, bez zapisu
    closeModal() {
      this.showModal = false;
    },
    // @vuese
    // Zapisuje konkurs
    saveContest(contest) {
      this.$store
        .dispatch(`contest/${contest.id ? 'update' : 'add'}`, contest)
        .then(contestId => {
          this.contest.$setData({
            contestId,
            contestName: contest.name,
            contestNameMsc: contest.declination.miejscownik,
            contestNameDop: contest.declination.dopelniacz
          });
          this.notifySuccess(Successes.CONTEST_SAVED);
          this.closeModal();
        })
        .catch(err => this.notifyError(Errors.CONTEST_CANNOT_SAVE));
    },
    // @vuese
    // Zapis id załącznika do schemy
    addAttachmentToSchema(attachment) {
      this.contest.attachmentIds.$addRow(attachment.id);
    },
    // @vuese
    // Usunięcie id załącznika z schemy
    removeAttachmentFromSchema(attachmentId) {
      const index = this.contest.attachmentIds.$model.indexOf(attachmentId);
      if (index > -1) {
        this.contest.attachmentIds.$items[index].$item.$removeRow();
      }
    }
  }
};
</script>

<style>
.teachers-table table tr td.state-error {
  background-color: #fd399517;
}

.teachers-table table tr td input {
  border: none;
  background-color: #ffffff00;
}
</style>
